import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Sparkles, Database, MessageSquarePlus, History, Loader2, MessageSquare, Trash2, Bell, ArrowUpRight, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useVickChat } from '../../hooks/useVickChat';
import ConfirmModal from '../ConfirmModal';
import type { VickConversation } from '../../../types';
import { useIaAlertas } from '../../hooks/useIaAlertas';
import { useAlertasLidos, alertaNaoLido } from '../../hooks/useAlertasLidos';
import AlertaCard from './AlertaCard';

interface VickChatPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

const markdownComponents = {
    h1: ({ children }: any) => <h1 className="text-sm font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-1 mb-2 mt-2 first:mt-0">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-2 mt-2 first:mt-0">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-xs font-semibold text-gray-800 dark:text-white mb-1 mt-2 first:mt-0">{children}</h3>,
    p: ({ children }: any) => <p className="text-gray-700 dark:text-slate-300 leading-relaxed mb-1.5 last:mb-0 text-sm">{children}</p>,
    strong: ({ children }: any) => <strong className="font-semibold text-gray-800 dark:text-white">{children}</strong>,
    em: ({ children }: any) => <em className="italic text-gray-700 dark:text-slate-300">{children}</em>,
    del: ({ children }: any) => <del className="line-through text-gray-500 dark:text-slate-400">{children}</del>,
    ul: ({ children }: any) => <ul className="list-disc list-inside space-y-0.5 mb-1.5 text-gray-700 dark:text-slate-300 text-sm">{children}</ul>,
    ol: ({ children }: any) => <ol className="list-decimal list-inside space-y-0.5 mb-1.5 text-gray-700 dark:text-slate-300 text-sm">{children}</ol>,
    li: ({ children }: any) => <li className="text-gray-700 dark:text-slate-300">{children}</li>,
    code: ({ className, children, ...props }: any) => (
        <code className="bg-gray-100 dark:bg-slate-700 text-primary px-1 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>
    ),
    pre: ({ children }: any) => <pre className="bg-gray-900 text-gray-100 rounded-lg p-2 mb-1.5 overflow-x-auto text-xs">{children}</pre>,
    a: ({ children, href }: any) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>,
};

const fmtData = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const VickChatPanel: React.FC<VickChatPanelProps> = ({ isOpen, onClose }) => {
    const {
        messages, conversationId, conversations, loadingConversations,
        sending, error, modo, setModo, sendMessage, resetConversation,
        loadConversations, openConversation, deleteConversation,
    } = useVickChat();
    const [input, setInput] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [aba, setAba] = useState<'conversar' | 'alertas'>('conversar');
    const { alertas, loading: carregandoAlertas, temAcesso: podeVerAlertas } = useIaAlertas();
    const { lidosAte, marcarLidos } = useAlertasLidos();
    // Derivado em render, não em estado: guardar a contagem duplicaria a fonte
    // e ela sairia do lugar assim que o realtime trouxesse um alerta novo.
    const naoLidos = alertas.filter(a => alertaNaoLido(a.detectado_em, lidosAte)).length;
    const [deleteTarget, setDeleteTarget] = useState<VickConversation | null>(null);
    const [deleting, setDeleting] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, sending]);

    // Esc fecha. O painel cobre a tela inteira com um backdrop, e sem isto a
    // única saída é acertar o X ou clicar fora — o que trava quem navega pelo
    // teclado. Pego abrindo a tela /vick com o painel por cima.
    useEffect(() => {
        if (!isOpen) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || sending) return;
        sendMessage(input);
        setInput('');
    };

    const toggleHistory = () => {
        const next = !showHistory;
        setShowHistory(next);
        if (next) loadConversations();
    };

    const handleNewConversation = () => {
        resetConversation();
        setShowHistory(false);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        await deleteConversation(deleteTarget.id);
        setDeleting(false);
        setDeleteTarget(null);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[150] bg-black/40 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="fixed bottom-24 left-5 z-[151] w-[400px] max-w-[calc(100vw-2.5rem)] h-[600px] max-h-[75vh] lg:w-[25vw] lg:min-w-[360px] lg:h-[90vh] lg:max-h-[90vh] lg:top-[5vh] lg:bottom-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden origin-bottom-left animate-scale-in"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-primary to-primary/80 text-white shrink-0">
                    <div className="flex items-center gap-2">
                        <Sparkles size={20} />
                        <div>
                            <h2 className="text-sm font-bold leading-tight">Vick</h2>
                            <p className="text-[11px] text-white/80 leading-tight">Assistente interna da Vigor Energy</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={toggleHistory} title="Histórico de conversas" className={`p-1.5 rounded-lg transition-colors ${showHistory ? 'bg-white/30' : 'hover:bg-white/20'}`}>
                            <History size={16} />
                        </button>
                        <button onClick={handleNewConversation} title="Nova conversa" className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                            <MessageSquarePlus size={16} />
                        </button>
                        <button onClick={onClose} title="Fechar" className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Abas. O contador fica na aba, não só no botão flutuante: quem
                    já abriu o painel para conversar precisa ver que apareceu algo.

                    Sem acesso a nenhum módulo de alerta a aba some inteira — a
                    fila viria vazia pela RLS, e uma aba que nunca tem nada é
                    pior que nenhuma aba. O acesso é a permissão "Vick — acesso
                    a dados" em Config > Permissões Especiais. */}
                {!showHistory && podeVerAlertas && (
                    <div className="flex border-b border-gray-200 dark:border-slate-700 shrink-0">
                        <button
                            onClick={() => setAba('conversar')}
                            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${aba === 'conversar' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-slate-300'}`}
                        >
                            Conversar
                        </button>
                        <button
                            onClick={() => setAba('alertas')}
                            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 flex items-center justify-center gap-1.5 ${aba === 'alertas' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-slate-300'}`}
                        >
                            Alertas
                            {/* Vermelho é o que ainda não foi lido; cinza é a fila que
                                continua aberta mas já passou pelos olhos. Um contador que
                                nunca zera para de ser lido — o alerta agregado de saldo
                                fica aberto por semanas. */}
                            {naoLidos > 0 ? (
                                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-px min-w-[18px]">
                                    {naoLidos > 99 ? '99+' : naoLidos}
                                </span>
                            ) : alertas.length > 0 ? (
                                <span className="bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-[10px] font-bold rounded-full px-1.5 py-px min-w-[18px]">
                                    {alertas.length > 99 ? '99+' : alertas.length}
                                </span>
                            ) : null}
                        </button>
                    </div>
                )}

                {!showHistory && podeVerAlertas && aba === 'alertas' ? (
                    /* Alertas do vigia — lista curta. A triagem (resolver, ignorar
                       com motivo, filtrar) acontece na tela cheia, que tem espaço. */
                    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                        {carregandoAlertas ? (
                            <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
                        ) : alertas.length === 0 ? (
                            <div className="text-center text-gray-400 dark:text-slate-600 text-sm mt-10 px-4">
                                <Bell size={28} className="mx-auto mb-2 opacity-50" />
                                Nada fora do lugar agora. Eu verifico de hora em hora e aviso se algo mudar.
                            </div>
                        ) : (
                            <>
                                {/* "Já li" não é "resolvido": ler não conserta nada. Serve
                                    para o contador parar de gritar sobre o que a pessoa já
                                    viu e decidiu deixar para depois. */}
                                <div className="flex items-center justify-between gap-2 pb-1">
                                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                                        {naoLidos > 0
                                            ? `${naoLidos} novo(s) desde a última vez`
                                            : 'Você já leu todos estes'}
                                    </p>
                                    {naoLidos > 0 ? (
                                        <button
                                            onClick={marcarLidos}
                                            className="inline-flex items-center gap-1 text-[11px] text-primary dark:text-primary-light hover:underline font-medium shrink-0"
                                        >
                                            <CheckCheck size={12} /> Marcar como lidos
                                        </button>
                                    ) : null}
                                </div>
                                {alertas.slice(0, 12).map(a => (
                                    <AlertaCard
                                        key={a.id}
                                        alerta={a}
                                        naoLido={alertaNaoLido(a.detectado_em, lidosAte)}
                                        onNavegar={onClose}
                                    />
                                ))}
                                <Link
                                    to="/vick"
                                    onClick={onClose}
                                    className="flex items-center justify-center gap-1.5 text-xs text-primary dark:text-primary-light hover:underline font-medium py-2"
                                >
                                    Ver todos e resolver <ArrowUpRight size={13} />
                                </Link>
                            </>
                        )}
                    </div>
                ) : showHistory ? (
                    /* Histórico */
                    <div className="flex-1 overflow-y-auto px-3 py-3">
                        {error && (
                            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 mb-3">{error}</div>
                        )}
                        {loadingConversations ? (
                            <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
                        ) : conversations.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 dark:text-slate-600 mt-10">Nenhuma conversa anterior ainda.</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {conversations.map(c => (
                                    <li key={c.id} className={`group flex items-center gap-1 rounded-xl transition-colors ${c.id === conversationId ? 'bg-primary/10 dark:bg-primary/20' : 'hover:bg-gray-100 dark:hover:bg-slate-800'}`}>
                                        <button
                                            onClick={() => { openConversation(c.id); setShowHistory(false); }}
                                            className="flex-1 min-w-0 flex items-start gap-2.5 text-left px-3 py-2.5"
                                        >
                                            <MessageSquare size={15} className="text-gray-400 mt-0.5 shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm text-gray-800 dark:text-white truncate">{c.titulo || 'Conversa sem título'}</p>
                                                <p className="text-[11px] text-gray-400 dark:text-slate-500">{fmtData(c.updated_at)}</p>
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => setDeleteTarget(c)}
                                            title="Excluir conversa"
                                            className="p-2 mr-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ) : (
                    /* Messages */
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                        {messages.length === 0 && (
                            <div className="text-center text-gray-400 dark:text-slate-600 text-sm mt-10 px-4">
                                <Sparkles size={28} className="mx-auto mb-2 opacity-50" />
                                Oi! Sou a Vick. Pergunte sobre a operação e eu consulto os dados do sistema na hora — "como está a carteira deste mês", "quanto vou receber em outubro", "alguma usina parada".
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${m.role === 'user'
                                    ? 'bg-primary text-white rounded-br-sm'
                                    : 'bg-gray-100 dark:bg-slate-800 rounded-bl-sm'}`}
                                >
                                    {m.role === 'assistant' ? (
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{m.content}</ReactMarkdown>
                                    ) : (
                                        <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                                    )}
                                    {m.sources && m.sources.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-700 flex flex-wrap gap-1.5">
                                            {m.sources.map((s, si) => (
                                                <span key={`${s.documentId}-${si}`} className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-900 px-2 py-0.5 rounded-full border border-gray-200 dark:border-slate-700">
                                                    <Database size={11} /> {s.titulo}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-gray-100 dark:bg-slate-800 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-gray-400 dark:text-slate-500">
                                    Vick está digitando...
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{error}</div>
                        )}
                    </div>
                )}

                {/* Input */}
                {!showHistory && (aba === 'conversar' || !podeVerAlertas) && (
                    <form onSubmit={handleSubmit} className="border-t border-gray-200 dark:border-slate-700 p-3 shrink-0">
                        <label className="flex items-center gap-1.5 mb-2 text-[11px] text-gray-500 dark:text-slate-400 cursor-pointer w-fit">
                            <input
                                type="checkbox"
                                checked={modo === 'planejamento'}
                                onChange={(e) => setModo(e.target.checked ? 'planejamento' : 'resposta')}
                                className="rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
                            />
                            Modo planejamento (resposta longa e estruturada)
                        </label>
                        <div className="flex items-end gap-2">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                placeholder="Pergunte algo para a Vick..."
                                rows={1}
                                className="flex-1 resize-none px-3 py-2.5 text-sm bg-gray-100 dark:bg-slate-800 border-0 rounded-xl focus:ring-2 focus:ring-primary outline-none dark:text-white max-h-28"
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || sending}
                                className="p-2.5 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white rounded-xl transition-colors shrink-0"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Excluir conversa"
                message={`Tem certeza que deseja excluir "${deleteTarget?.titulo || 'esta conversa'}"? Todas as mensagens serão apagadas permanentemente e não será possível recuperá-las.`}
                confirmLabel="Excluir"
                variant="danger"
                loading={deleting}
            />
        </div>,
        document.body
    );
};

export default VickChatPanel;
